import React from "react";
import "./styles.css";

export type UserOption = {
    id: string;
    label: string;
    subtitle?: string;
};

export type UserSelectAddProps = {
    header?: string;
    users: UserOption[];
    valueId?: string | null;

    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;

    onChange: (userId: string | null) => void;
    onAddNew?: (typedText: string) => void;

    className?: string;
    hideHint?: boolean;
};

function UserSelectAdd({
    header = "",
    users,
    valueId = null,
    placeholder = "Vyberte zaměstnance…",
    disabled = false,
    readOnly = false,
    onChange,
    onAddNew,
    className = "",
    hideHint = false
}: UserSelectAddProps) {
    const rootRef = React.useRef<HTMLDivElement | null>(null);

    const selected = React.useMemo(() => {
        const vid = valueId == null ? null : String(valueId);
        return users.find(u => String(u.id) === vid) ?? null;
    }, [users, valueId]);

    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [highlightIndex, setHighlightIndex] = React.useState(0);

    const effectiveDisabled = disabled || readOnly;

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;

        return users.filter((u) => {
            const hay = `${u.label} ${u.subtitle ?? ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [users, query]);

    React.useEffect(() => {
        setHighlightIndex((i) => Math.max(0, Math.min(i, Math.max(0, filtered.length - 1))));
    }, [filtered.length]);

    React.useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, []);

    const commitSelect = (id: string) => {
        onChange(id);
        setOpen(false);
        setQuery("");
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (effectiveDisabled) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            setHighlightIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            if (!open) {
                setOpen(true);
                return;
            }
            const item = filtered[highlightIndex];
            if (item) commitSelect(item.id);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const canClear = !!query || !!valueId;

    const clearSelection = () => {
        setQuery("");
        onChange(null);
        setOpen(false);
        setHighlightIndex(0);
        inputRef.current?.focus();
    };

    return (
        <div
            ref={rootRef}
            className={`user-select-add ${className}`}
            aria-disabled={effectiveDisabled ? "false" : "false"}
        >
            {header && (
                <div className="user-select-add__header">
                    {header}
                </div>
            )}

            <div className="user-select-add__box">
                <div className="user-select-add__inputWrap">
                    <input
                        className="user-select-add__input"
                        type="text"
                        disabled={effectiveDisabled}
                        placeholder={effectiveDisabled ? "Načtěte kartu zaměstnance..." : placeholder}
                        value={open ? query : selected?.label ?? ""}
                        onFocus={() => !effectiveDisabled && setOpen(true)}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                            setHighlightIndex(0);
                            if (!e.target.value) {
                                onChange(null);
                            }
                        }}
                        onKeyDown={onKeyDown}
                        aria-expanded={open}
                        aria-haspopup="listbox"
                    />

                    <button
                        type="button"
                        className="user-select-add__caret"
                        onClick={() => !effectiveDisabled && setOpen((v) => !v)}
                        disabled={effectiveDisabled}
                        aria-label="Otevřít seznam"
                        tabIndex={-1}
                    >
                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                            <path
                                d="M7 10l5 5 5-5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                {onAddNew && !effectiveDisabled && <button
                    type="button"
                    className="user-select-add__plus"
                    onClick={() => onAddNew((open ? query : selected?.label ?? "").trim())}
                    disabled={effectiveDisabled}
                    aria-label="Přidat nového zaměstnance"
                    title="Přidat nového zaměstnance"
                >
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path
                            d="M12 5v14M5 12h14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>}

                {canClear && !effectiveDisabled && (
                    <button
                        type="button"
                        className="user-select-add__clear"
                        onClick={clearSelection}
                        aria-label="Vymazat výběr"
                        title="Vymazat"
                        tabIndex={-1}
                    >
                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                            <path
                                d="M6 6l10 10M16 6l-10 10"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                )}
            </div>

            {open && !effectiveDisabled && (
                <div className={header ? "user-select-add__dropdown" : "user-select-add__dropdown__noheader"} role="listbox">
                    {filtered.length === 0 ? (
                        <div className="user-select-add__empty">
                            Nenalezen žádný uživatel
                        </div>
                    ) : (
                        filtered.map((u, i) => (
                            <button
                                type="button"
                                key={u.id}
                                className={`user-select-add__item ${i === highlightIndex ? "is-active" : ""
                                    } ${u.id === valueId ? "is-selected" : ""}`}
                                onMouseEnter={() => setHighlightIndex(i)}
                                onClick={() => commitSelect(u.id)}
                                role="option"
                                aria-selected={u.id === valueId}
                            >
                                <div className="user-select-add__itemTitle">{u.label}</div>
                                {u.subtitle && (
                                    <div className="user-select-add__itemSub">{u.subtitle}</div>
                                )}
                            </button>
                        ))
                    )}
                </div>
            )}

            {header && !effectiveDisabled && !hideHint && <div className="user-select-add__hint">
                Načtěte kartu zaměstnance nebo přidejte nového
            </div>}
        </div>
    );
}

export default UserSelectAdd;